import ClientPricingCategoryMultiSelect from './clientPricing/ClientPricingCategoryMultiSelect';
import BulkPricingPendingSection from './bulkPricing/BulkPricingPendingSection';
import useClientPricingBulkDraft from '../hooks/useClientPricingBulkDraft';
import { hasPermission } from '../utils/permissions';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiTag } from 'react-icons/fi';
import {
  getClientCustomPricingDiscountCatalog,
  getClientPricingDiscountCatalog,
  getClientPricingSettings,
  previewClientPricingSelection,
} from '../api';
import useDebouncedCallback from '../hooks/useDebouncedCallback';
import useProductEvents from '../hooks/useProductEvents';
import {
  getSelectedBranchId,
  subscribeSelectedBranch,
} from '../utils/selectedBranch';
import ClientPricingDiscountMatrix from './clientPricing/ClientPricingDiscountMatrix';
import {
  applyClientPricingBulkDraft,
  CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
  CLIENT_PRICING_MODE_DISCOUNT,
  CLIENT_PRICING_RULE_FIXED,
  CLIENT_PRICING_RULE_MARGIN,
  hasClientPricingRule,
  isClientPricingMarginRule,
  mapClientPricingCatalogRowsToDiscounts,
  mapClientPricingDiscountsToList,
  mergeClientPricingRowsWithDrafts,
  toClientPricingDiscountMap,
} from '../utils/clientPricingMatrix';

const ClientPricingDiscountConfigurator = ({
  mode = 'none',
  onModeChange,
  categoryValue: legacyCategoryValue = '',
  categoryValues,
  onCategoriesChange,
  onCategoryChange,
  categories = [],
  categoriesLoading = false,
  customDiscounts = [],
  onCustomDiscountsChange,
  saveAsCategory = false,
  onSaveAsCategoryChange,
  categoryName = '',
  onCategoryNameChange,
  categoryDescription = '',
  onCategoryDescriptionChange,
  clientId = null,
  onReadyChange,
  onDraftChange,
  currentUser,
  disabled = false,
  className = '',
}) => {
  const categoryValue = categoryValues?.length === 1 ? String(categoryValues[0]) : legacyCategoryValue;
  const categorySelectionKey = categoryValues?.join(',') || categoryValue;
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [rowsError, setRowsError] = useState('');
  const [search, setSearch] = useState('');
  const [bulkForm, setBulkForm] = useState({ discountType: 'amount', discountValue: '' });
  const [pricingSettings, setPricingSettings] = useState({
    mode: CLIENT_PRICING_MODE_DISCOUNT,
    marginPercentDisplay: CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
  });
  const [pricingSettingsError, setPricingSettingsError] = useState('');
  const [pricingSettingsLoading, setPricingSettingsLoading] = useState(true);
  const [referenceBranchId, setReferenceBranchId] = useState(getSelectedBranchId);
  const [resolvedCatalogBranchId, setResolvedCatalogBranchId] = useState('');
  const [catalogRefreshVersion, setCatalogRefreshVersion] = useState(0);
  const [draftReady, setDraftReady] = useState(false);
  const [templateCategoryId, setTemplateCategoryId] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const initialLoadRef = useRef(false);
  const autoTemplateCategoryRef = useRef('');
  const customDiscountsRef = useRef(customDiscounts);
  const mountedRef = useRef(true);
  const modeRef = useRef(mode);
  const categoryValueRef = useRef(categorySelectionKey);
  const onModeChangeRef = useRef(onModeChange);
  const onCustomDiscountsChangeRef = useRef(onCustomDiscountsChange);
  const templateRequestRef = useRef(0);
  const bulkScopeKey = `${clientId || 'new'}:${referenceBranchId || resolvedCatalogBranchId}:${currentUser?.id || ''}`;
  const bulkScopeRef = useRef({ key: bulkScopeKey });
  if (bulkScopeRef.current.key !== bulkScopeKey) bulkScopeRef.current = { key: bulkScopeKey };

  useEffect(() => {
    customDiscountsRef.current = customDiscounts;
  }, [customDiscounts]);

  useEffect(() => {
    let mounted = true;
    getClientPricingSettings()
      .then((settings) => {
        if (!mounted) return;
        setPricingSettings({
          mode: settings?.mode || CLIENT_PRICING_MODE_DISCOUNT,
          marginPercentDisplay: settings?.marginPercentDisplay || CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
        });
        setPricingSettingsError('');
      })
      .catch((err) => {
        if (!mounted) return;
        setPricingSettingsError(
          err?.response?.data?.message || 'No se pudo cargar el modo de edición global. Se mostrará el editor de descuentos fijos.'
        );
      })
      .finally(() => {
        if (mounted) setPricingSettingsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const [bulkRefresh, setBulkRefresh] = useState(0);
  const pricingDraft = useClientPricingBulkDraft({
    enabled: Boolean(onDraftChange) && !disabled,
    userId: currentUser?.id, clientId, branchId: referenceBranchId || resolvedCatalogBranchId,
    discounts: customDiscounts,
    metadata: { mode, saveAsCategory, categoryName, categoryDescription },
    onDraftChange,
    onRestore: (saved) => {
      customDiscountsRef.current = saved.discounts;
      onCustomDiscountsChange?.(saved.discounts);
      onModeChange?.(saved.metadata?.mode || 'custom');
      onSaveAsCategoryChange?.(Boolean(saved.metadata?.saveAsCategory));
      onCategoryNameChange?.(saved.metadata?.categoryName || '');
      onCategoryDescriptionChange?.(saved.metadata?.categoryDescription || '');
    },
  });
  const editorReady = !pricingDraft.busy && !pricingDraft.dirty && !pricingDraft.error && (mode !== 'custom'
    || (draftReady && !loadingRows && !templateLoading && !pricingSettingsLoading));

  useEffect(() => {
    onReadyChange?.(editorReady);
  }, [editorReady, onReadyChange]);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => subscribeSelectedBranch(setReferenceBranchId), []);

  useEffect(() => {
    modeRef.current = mode;
    categoryValueRef.current = categorySelectionKey;
    onModeChangeRef.current = onModeChange;
    onCustomDiscountsChangeRef.current = onCustomDiscountsChange;
  }, [categorySelectionKey, mode, onCustomDiscountsChange, onModeChange]);

  useEffect(() => {
    initialLoadRef.current = false;
    autoTemplateCategoryRef.current = '';
    setDraftReady(false);
    setTemplateCategoryId('');
  }, [clientId]);

  useEffect(() => {
    autoTemplateCategoryRef.current = '';
  }, [categorySelectionKey]);

  const { schedule: scheduleCatalogRefresh } = useDebouncedCallback(() => {
    if (!mountedRef.current) return;
    if (modeRef.current !== 'custom' && !clientId) return;
    setCatalogRefreshVersion((version) => version + 1);
  }, 250);

  const handleCatalogEvent = useCallback((event) => {
    if (event?.type !== 'products.invalidated') return;
    scheduleCatalogRefresh();
  }, [scheduleCatalogRefresh]);

  useProductEvents(referenceBranchId || resolvedCatalogBranchId, handleCatalogEvent);

  const applyTemplateCategory = useCallback(async (categoryId, { auto = false } = {}) => {
    const normalizedCategoryId = String(categoryId || '').trim();
    const parsedCategoryId = Number(normalizedCategoryId);
    if (!Number.isInteger(parsedCategoryId)) return;

    const requestId = templateRequestRef.current + 1;
    templateRequestRef.current = requestId;
    setTemplateLoading(true);
    setRowsError('');
    setTemplateCategoryId(normalizedCategoryId);

    try {
      const data = await getClientPricingDiscountCatalog(parsedCategoryId);
      if (!mountedRef.current || templateRequestRef.current !== requestId) return;
      const nextDiscounts = mapClientPricingCatalogRowsToDiscounts(data?.rows);
      customDiscountsRef.current = nextDiscounts;
      onCustomDiscountsChangeRef.current?.(nextDiscounts);
      setDraftReady(true);
    } catch (err) {
      if (!mountedRef.current || templateRequestRef.current !== requestId) return;
      setRowsError(err?.response?.data?.message || (
        auto
          ? 'No se pudieron precargar los descuentos de la categoria actual.'
          : 'No se pudieron cargar los descuentos de la plantilla.'
      ));
    } finally {
      if (mountedRef.current && templateRequestRef.current === requestId) {
        setTemplateLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const currentCategoryId = String(categoryValue || '').trim();
    if (mode !== 'custom' || !currentCategoryId) return;
    if (autoTemplateCategoryRef.current === currentCategoryId) return;
    autoTemplateCategoryRef.current = currentCategoryId;
    setTemplateCategoryId(currentCategoryId);
    const hasDraftDiscounts = customDiscountsRef.current.some(hasClientPricingRule);
    if (!hasDraftDiscounts) {
      applyTemplateCategory(currentCategoryId, { auto: true });
    }
  }, [applyTemplateCategory, categoryValue, mode]);

  useEffect(() => {
    if (mode !== 'custom' || !categoryValues || categoryValues.length < 2
      || autoTemplateCategoryRef.current === categorySelectionKey
      || customDiscountsRef.current.some(hasClientPricingRule)) return;
    autoTemplateCategoryRef.current = categorySelectionKey;
    const requestId = ++templateRequestRef.current;
    setTemplateLoading(true);
    setRowsError('');
    previewClientPricingSelection(categoryValues, { branchId: referenceBranchId || undefined }).then((data) => {
      if (!mountedRef.current || requestId !== templateRequestRef.current) return;
      const nextDiscounts = mapClientPricingCatalogRowsToDiscounts(data.discounts || []);
      customDiscountsRef.current = nextDiscounts;
      onCustomDiscountsChangeRef.current?.(nextDiscounts);
      setDraftReady(true);
    }).catch((error) => {
      if (mountedRef.current && requestId === templateRequestRef.current) setRowsError(error?.response?.data?.message || 'No se pudieron cargar las categorías seleccionadas.');
    }).finally(() => {
      if (mountedRef.current && requestId === templateRequestRef.current) setTemplateLoading(false);
    });
  }, [categorySelectionKey, categoryValues, mode, referenceBranchId]);

  useEffect(() => {
    let mounted = true;
    const loadRows = async () => {
      if (mode !== 'custom' && !clientId) {
        setRows([]);
        setRowsError('');
        setDraftReady(true);
        return;
      }
      setLoadingRows(true);
      setRowsError('');
      try {
        const data = await getClientCustomPricingDiscountCatalog({
          ...(clientId ? { clientId } : {}),
          ...(referenceBranchId ? { branchId: referenceBranchId } : {}),
          ...(search.trim() ? { search: search.trim() } : {}),
        });
        if (!mounted) return;
        setResolvedCatalogBranchId(String(data?.branchId || referenceBranchId || ''));
        const nextRows = Array.isArray(data?.rows) ? data.rows : [];
        setRows(nextRows);
        if (!initialLoadRef.current) {
          const serverDiscounts = mapClientPricingCatalogRowsToDiscounts(nextRows);
          if (serverDiscounts.length > 0) {
            const merged = toClientPricingDiscountMap(customDiscountsRef.current);
            serverDiscounts.forEach((discount) => {
              if (!merged.has(Number(discount.varietyPriceId))) {
                merged.set(Number(discount.varietyPriceId), discount);
              }
            });
            onCustomDiscountsChangeRef.current?.(mapClientPricingDiscountsToList(merged));
            if (modeRef.current === 'none' && !categoryValueRef.current) {
              onModeChangeRef.current?.('custom');
            }
          }
          initialLoadRef.current = true;
          setDraftReady(true);
        }
      } catch (err) {
        if (!mounted) return;
        setRows([]);
        setRowsError(err?.response?.data?.message || 'No se pudieron cargar los descuentos personalizados.');
      } finally {
        if (mounted) setLoadingRows(false);
      }
    };
    loadRows();
    return () => {
      mounted = false;
    };
  }, [catalogRefreshVersion, clientId, mode, referenceBranchId, search]);

  const selectedCategoryIds = categoryValues || (categoryValue ? [Number(categoryValue)] : []);
  const visibleRows = useMemo(() => mergeClientPricingRowsWithDrafts({
    rows,
    discounts: customDiscounts,
    draftReady,
  }), [customDiscounts, draftReady, rows]);

  const updateDiscount = (varietyPriceId, patch) => {
    const id = Number(varietyPriceId);
    if (!Number.isInteger(id)) return;
    const nextMap = toClientPricingDiscountMap(customDiscountsRef.current);
    const current = nextMap.get(id) || {
      varietyPriceId: id,
      ruleType: CLIENT_PRICING_RULE_FIXED,
      discountType: 'amount',
      discountValue: '',
    };
    const next = {
      ...current,
      ...patch,
      varietyPriceId: id,
      ...(patch?.ruleType === CLIENT_PRICING_RULE_FIXED ? { marginReductionPercent: null } : {}),
    };
    if (hasClientPricingRule(next)) {
      nextMap.set(id, isClientPricingMarginRule(next) ? {
        varietyPriceId: id,
        ruleType: CLIENT_PRICING_RULE_MARGIN,
        marginReductionPercent: next.marginReductionPercent,
      } : {
        varietyPriceId: id,
        ruleType: CLIENT_PRICING_RULE_FIXED,
        discountType: next.discountType || 'amount',
        discountValue: next.discountValue,
        ...(next.replaceExistingRule === true ? { replaceExistingRule: true } : {}),
      });
    } else if (isClientPricingMarginRule(current)) {
      nextMap.set(id, {
        varietyPriceId: id,
        ruleType: CLIENT_PRICING_RULE_MARGIN,
        marginReductionPercent: 0,
      });
    } else {
      nextMap.delete(id);
    }
    const nextList = mapClientPricingDiscountsToList(nextMap);
    customDiscountsRef.current = nextList;
    onCustomDiscountsChange?.(nextList);
  };

  const applySavedCorrection = (summary, item) => {
    const completed = (item ? [item] : summary?.resolvedItems || []).filter((entry) => ['applied', 'unchanged'].includes(entry.status));
    if (completed.length) {
      const merged = toClientPricingDiscountMap(customDiscountsRef.current);
      for (const entry of completed) {
        const discount = entry.snapshot?.state?.discount;
        merged.set(Number(entry.varietyPriceId), discount
          ? { varietyPriceId: Number(entry.varietyPriceId), ...discount }
          : { varietyPriceId: Number(entry.varietyPriceId), ruleType: CLIENT_PRICING_RULE_MARGIN, marginReductionPercent: 0 });
      }
      const next = mapClientPricingDiscountsToList(merged);
      customDiscountsRef.current = next;
      onCustomDiscountsChangeRef.current?.(next);
      if (modeRef.current === 'none' && !categoryValueRef.current && completed.some((entry) => entry.snapshot?.state?.discount)) onModeChangeRef.current?.('custom');
    }
    setCatalogRefreshVersion((value) => value + 1);
  };

  const applyBulk = async ({
    action,
    selectedRows,
    discountType,
    discountValue,
    ruleType,
    marginReductionPercent,
    replaceExistingRules,
    benefitType,
    benefitValue,
  }) => {
    if (onDraftChange) {
      const owner = bulkScopeRef.current;
      try {
        await pricingDraft.applyBulk({ action, varietyPriceIds: selectedRows.map((row) => Number(row.varietyPriceId)), discountType, discountValue, ruleType, marginReductionPercent, replaceExistingRules, benefitType, benefitValue });
        if (!mountedRef.current || bulkScopeRef.current !== owner) return;
        setBulkRefresh((value) => value + 1);
        setRowsError('');
      } catch (error) { if (mountedRef.current && bulkScopeRef.current === owner) setRowsError(error?.response?.data?.message || error.message || 'No se pudo aplicar el cambio al borrador.'); }
      return;
    }
    const marginRule = action === 'set_margin_discount' || ruleType === CLIENT_PRICING_RULE_MARGIN;
    const value = marginRule ? marginReductionPercent : discountValue;
    if (action !== 'clear' && marginRule && Number(value) === 0) {
      const cleared = applyClientPricingBulkDraft({
        discounts: customDiscountsRef.current,
        rows: selectedRows,
        action: 'clear',
      });
      customDiscountsRef.current = cleared;
      onCustomDiscountsChange?.(cleared);
      setRowsError('');
      return;
    }
    if (action !== 'clear' && !(Number(value) > 0)) {
      setRowsError(marginRule ? 'Ingresa un porcentaje válido.' : 'Ingresa un descuento mayor a 0.');
      return;
    }
    const nextList = applyClientPricingBulkDraft({
      discounts: customDiscountsRef.current,
      rows: selectedRows,
      action,
      ruleType: marginRule ? CLIENT_PRICING_RULE_MARGIN : CLIENT_PRICING_RULE_FIXED,
      marginReductionPercent,
      replaceExistingRules,
      discountType,
      discountValue,
    });
    customDiscountsRef.current = nextList;
    onCustomDiscountsChange?.(nextList);
    setRowsError('');
  };

  const activeDiscountCount = customDiscounts.filter(hasClientPricingRule).length;
  const selectedTemplateCategory = categories.find((category) => String(category.id) === String(templateCategoryId));
  const statusLabel = mode === 'category'
    ? selectedCategoryIds.length ? `${selectedCategoryIds.length} categorías` : 'Seleccionar categorías'
    : mode === 'custom'
      ? activeDiscountCount > 0 ? `${activeDiscountCount} descuentos` : 'Personalizado'
      : 'Sin descuento';

  return (
    <section className={`rounded-lg border border-blue-100 bg-blue-50/50 p-4 ${className}`.trim()}>
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-blue-100 text-blue-700">
          <FiTag />
        </div>
        <div className="min-w-0 flex-1 space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <label className="text-xs font-semibold uppercase text-gray-600">
                Descuentos de cliente
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Aplica descuentos automaticos sobre precios por formato.
              </p>
            </div>
            <span className={`w-fit rounded-full px-2 py-1 text-[11px] font-semibold ${
              mode === 'none'
                ? 'bg-gray-100 text-gray-600'
                : 'bg-emerald-100 text-emerald-700'
            }`}
            >
              {statusLabel}
            </span>
          </div>

          <div className="grid gap-2 sm:grid-cols-3">
            {[
              { value: 'none', label: 'Sin descuento' },
              { value: 'category', label: 'Categoria' },
              { value: 'custom', label: 'Personalizado' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onModeChange?.(option.value)}
                disabled={disabled || pricingDraft.busy}
                className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                  mode === option.value
                    ? 'border-blue-400 bg-white text-blue-700 ring-2 ring-blue-100'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {mode === 'category' && (
            <div className="space-y-3">
              <ClientPricingCategoryMultiSelect
                value={selectedCategoryIds}
                onChange={(ids) => onCategoriesChange ? onCategoriesChange(ids) : onCategoryChange?.({ target: { name: 'clientPricingCategoryId', value: ids[0] || '' } })}
                categories={categories}
                loading={categoriesLoading}
                disabled={disabled || pricingDraft.busy}
                marginPercentDisplay={pricingSettings.marginPercentDisplay}
              />
              {!categoriesLoading && categories.length === 0 && (
                <p className="rounded border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  No hay categorias activas. Crealas en Configuracion, Clientes.
                </p>
              )}
            </div>
          )}

          {mode === 'custom' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-600">
                      Plantilla de categoria
                    </label>
                    <p className="text-xs text-gray-500">
                      Precarga descuentos desde una categoria y luego ajustalos como personalizados.
                    </p>
                  </div>
                  {selectedTemplateCategory && (
                    <span className="w-fit rounded-full bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700">
                      {selectedTemplateCategory.name}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <select
                    value={templateCategoryId}
                    onChange={(event) => setTemplateCategoryId(event.target.value)}
                    className="h-10 min-w-0 flex-1 rounded border border-gray-300 bg-white px-3 text-sm"
                    disabled={disabled || categoriesLoading || templateLoading}
                  >
                    <option value="">{categoriesLoading ? 'Cargando categorias...' : 'Seleccionar plantilla'}</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => applyTemplateCategory(templateCategoryId)}
                    disabled={disabled || templateLoading || !templateCategoryId}
                    className="h-10 rounded border border-blue-200 bg-blue-50 px-3 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {templateLoading ? 'Cargando...' : 'Aplicar plantilla'}
                  </button>
                </div>
                {selectedTemplateCategory?.description && (
                  <p className="mt-2 rounded border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-gray-600">
                    {selectedTemplateCategory.description}
                  </p>
                )}
              </div>

              <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(saveAsCategory)}
                  onChange={(event) => onSaveAsCategoryChange?.(event.target.checked)}
                  disabled={disabled || pricingDraft.busy}
                />
                Convertir en categoria al guardar
              </label>

              {saveAsCategory && (
                <div className="grid gap-3 rounded-lg border border-blue-100 bg-white p-3 md:grid-cols-2">
                  <input
                    type="text"
                    value={categoryName}
                    onChange={(event) => onCategoryNameChange?.(event.target.value)}
                    placeholder="Nombre de categoria"
                    className="h-10 rounded border border-gray-300 px-3 text-sm"
                    disabled={disabled || pricingDraft.busy}
                  />
                  <input
                    type="text"
                    value={categoryDescription}
                    onChange={(event) => onCategoryDescriptionChange?.(event.target.value)}
                    placeholder="Descripcion opcional"
                    className="h-10 rounded border border-gray-300 px-3 text-sm"
                    disabled={disabled || pricingDraft.busy}
                  />
                </div>
              )}

              <ClientPricingDiscountMatrix
                title="Descuentos personalizados"
                description="Selecciona precios y configura descuentos por formato."
                pricingMode={pricingSettings.mode}
                marginPercentDisplay={pricingSettings.marginPercentDisplay}
                rows={visibleRows}
                loading={loadingRows}
                error={rowsError || pricingDraft.error || pricingSettingsError}
                disabled={disabled || pricingDraft.busy}
                savingBulk={pricingDraft.busy}
                customMode
                search={search}
                onSearchChange={setSearch}
                bulkForm={bulkForm}
                onBulkFormChange={setBulkForm}
                onBulkAction={applyBulk}
                onRowChange={updateDiscount}
                emptyMessage="No hay precios visibles para este filtro."
              />
              {pricingDraft.error && <div className="flex flex-wrap gap-3 text-sm">
                <button type="button" className="rounded-lg border px-3 py-2" disabled={disabled || pricingDraft.busy} onClick={pricingDraft.retrySave}>Reintentar guardado del borrador</button>
                {pricingDraft.draft && <button type="button" className="rounded-lg border px-3 py-2" disabled={disabled || pricingDraft.busy} onClick={pricingDraft.reload}>Descartar ediciones locales y recargar borrador</button>}
              </div>}
              {pricingDraft.draft && <BulkPricingPendingSection key={`draft:${pricingDraft.draft.id}`} scope={{ scope: 'client_draft', branchId: pricingDraft.draft.branchId, draftId: pricingDraft.draft.id }} refreshKey={bulkRefresh}
                onApplied={async () => { await pricingDraft.reload(); setCatalogRefreshVersion((value) => value + 1); }} disabled={disabled || pricingDraft.busy || pricingDraft.dirty}
                canUpdateProduct={hasPermission(currentUser, 'product.update')} canUpdatePrice={hasPermission(currentUser, 'price.update')} userId={currentUser?.id} />}

            </div>
          )}
              {clientId && onDraftChange && (referenceBranchId || resolvedCatalogBranchId) && <BulkPricingPendingSection key={`client:${clientId}:${referenceBranchId || resolvedCatalogBranchId}`} scope={{ scope: 'client', branchId: referenceBranchId || resolvedCatalogBranchId, clientId }}
                onApplied={applySavedCorrection} disabled={disabled || pricingDraft.busy || pricingDraft.dirty}
                canUpdateProduct={hasPermission(currentUser, 'product.update')} canUpdatePrice={hasPermission(currentUser, 'price.update')} userId={currentUser?.id} />}
        </div>
      </div>
    </section>
  );
};

export default ClientPricingDiscountConfigurator;
